import { useCallback } from "react";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";
import { Paginated, PaginationQuery } from "../types";
import { buildUrl, mutateCache, useApi } from "./api";
import { CustomError } from "./errors";
import {
  ConfigurationSharingAccess,
  ConfigurationSharingAccessInviteConfigurationData,
  InsertConfigurationSharingAccessInviteBody,
  UpdateConfigurationSharingAccessBody,
} from "./types/configurationSharingAccesses";

export const useConfigurationSharingAccessesApi = () => {
  const api = useApi();

  const getBaseUrl = useCallback((configurationId: string, isAdmin?: boolean) => {
    return `${isAdmin ? "/admin" : ""}/configurations/${configurationId}/sharing_accesses`;
  }, []);

  const mutateConfigurationSharingAccesses = useCallback((configurationId: string) => {
    mutateCache([
      `/configurations`,
      `/admin/configurations`,
      `/configurations/${configurationId}/sharing_accesses`,
      `/admin/configurations/${configurationId}/sharing_accesses`,
    ]);
  }, []);

  const useSearchConfigurationSharingAccesses = useCallback(
    (configurationId: string, query: PaginationQuery, signal: AbortSignal, isAdmin?: boolean) => {
      const url = buildUrl(getBaseUrl(configurationId, isAdmin), query);
      return useSWR<Paginated<ConfigurationSharingAccess>, CustomError, string>(url, (url) =>
        api.get<Paginated<ConfigurationSharingAccess>>({ url, signal }),
      );
    },
    [api, getBaseUrl],
  );

  const useConfigurationSharingAccessInviteConfigurationData = useCallback(
    (token: string) => {
      const encodedToken = encodeURIComponent(token);
      const url = `/configurations/sharing_accesses/invites/${encodedToken}/configuration_data`;
      return useSWRImmutable<ConfigurationSharingAccessInviteConfigurationData, CustomError, string>(url, (url) =>
        api.get<ConfigurationSharingAccessInviteConfigurationData>({ url }),
      );
    },
    [api],
  );

  const insertConfigurationSharingAccessInvite = useCallback(
    async (
      configurationId: string,
      body: InsertConfigurationSharingAccessInviteBody,
      isAdmin?: boolean,
    ): Promise<void> => {
      await api.post<ConfigurationSharingAccess, InsertConfigurationSharingAccessInviteBody>(
        `${getBaseUrl(configurationId, isAdmin)}/invites`,
        body,
      );
      mutateConfigurationSharingAccesses(configurationId);
    },
    [api, getBaseUrl, mutateConfigurationSharingAccesses],
  );

  const resendConfigurationSharingAccessInvite = useCallback(
    async (configurationId: string, accessId: string, isAdmin?: boolean): Promise<void> => {
      await api.postNoResult(`${getBaseUrl(configurationId, isAdmin)}/invites/${accessId}/re_send`);
      mutateConfigurationSharingAccesses(configurationId);
    },
    [api, getBaseUrl, mutateConfigurationSharingAccesses],
  );

  const cancelConfigurationSharingAccessInvite = useCallback(
    async (configurationId: string, accessId: string): Promise<void> => {
      await api.put(`${getBaseUrl(configurationId)}/invites/${accessId}/cancel`, {});
      mutateConfigurationSharingAccesses(configurationId);
    },
    [api, getBaseUrl, mutateConfigurationSharingAccesses],
  );

  const acceptConfigurationSharingAccessInvite = useCallback(
    async (token: string): Promise<{ configurationId: string }> => {
      const result = await api.post<{ configurationId: string }, { token: string }>(
        `/configurations/sharing_accesses/invites/accept`,
        { token },
      );
      mutateConfigurationSharingAccesses(result.configurationId);
      return result;
    },
    [api, mutateConfigurationSharingAccesses],
  );

  const rejectConfigurationSharingAccessInvite = useCallback(
    async (token: string): Promise<void> => {
      await api.postNoResult(`/configurations/sharing_accesses/invites/reject`, { token });
      mutateCache([`/configurations`, `/admin/configurations`]);
    },
    [api],
  );

  const updateConfigurationSharingAccess = useCallback(
    async (
      configurationId: string,
      accessId: string,
      body: UpdateConfigurationSharingAccessBody,
      isAdmin?: boolean,
    ): Promise<void> => {
      await api.put<UpdateConfigurationSharingAccessBody>(`${getBaseUrl(configurationId, isAdmin)}/${accessId}`, body);
      mutateConfigurationSharingAccesses(configurationId);
    },
    [api, getBaseUrl, mutateConfigurationSharingAccesses],
  );

  const deleteConfigurationSharingAccess = useCallback(
    async (configurationId: string, accessId: string, isAdmin?: boolean): Promise<void> => {
      await api.del(`${getBaseUrl(configurationId, isAdmin)}/${accessId}`);
      mutateConfigurationSharingAccesses(configurationId);
    },
    [api, getBaseUrl, mutateConfigurationSharingAccesses],
  );

  return {
    useSearchConfigurationSharingAccesses,
    useConfigurationSharingAccessInviteConfigurationData,
    insertConfigurationSharingAccessInvite,
    resendConfigurationSharingAccessInvite,
    cancelConfigurationSharingAccessInvite,
    acceptConfigurationSharingAccessInvite,
    rejectConfigurationSharingAccessInvite,
    updateConfigurationSharingAccess,
    deleteConfigurationSharingAccess,
  };
};
